/**
 * Aegis Account Integrity Service
 * Trust-first, abuse-resistant account security
 * 
 * Implements:
 * - Session limits with gentle enforcement
 * - Trusted device management
 * - Silent anomaly detection
 * - Account health scoring
 */

import { getSupabaseClient } from "@/lib/supabase/loader";
import type { RpcFunctions } from "@/lib/supabase-types";
// Device fingerprinting is centralized in src/lib/deviceFingerprint.ts
import type { PostgrestError } from "@supabase/supabase-js";

// Type definitions for database operations
interface TrustedDeviceRow {
  id: string;
  user_id: string;
  device_id: string;
  device_name: string;
  os_name: string;
  os_version: string;
  browser_name: string;
  browser_version: string;
  verified_at: string;
  first_used_at: string;
  last_used_at: string;
  total_uses: number;
  is_active: boolean;
  revoked_at?: string;
}

interface PlanLimitRow {
  plan_name: string;
  max_active_sessions: number;
  device_verification_required: boolean;
  max_trusted_devices: number;
}

interface UserSessionRow {
  id: string;
  user_id: string;
  session_token: string;
  device_id: string;
  device_name: string;
  os_name: string;
  os_version: string;
  browser_name: string;
  browser_version: string;
  ip_address?: string;
  is_trusted_device: boolean;
  created_at: string;
  last_activity_at: string;
  expires_at: string;
  ended_at?: string;
}

interface AccountHealthScoreRow {
  user_id: string;
  health_score: number;
  health_band: "green" | "yellow" | "orange" | "red";
  session_score: number;
  device_score: number;
  anomaly_score: number;
  verification_score: number;
  restriction_level: "none" | "gentle" | "moderate" | "strict";
  session_overlap_count: number;
  device_churn_count: number;
  geo_anomaly_count: number;
  updated_at: string;
}

// ============================================
// Types
// ============================================

export interface UserSession {
  id: string;
  userId: string;
  sessionToken: string;
  deviceId: string;
  deviceName: string;
  osName: string;
  osVersion: string;
  browserName: string;
  browserVersion: string;
  ipAddress: string;
  geoCountry?: string;
  geoCity?: string;
  isTrustedDevice: boolean;
  createdAt: string;
  lastActivityAt: string;
  expiresAt: string;
  endedAt?: string;
}

export interface TrustedDevice {
  id: string;
  userId: string;
  deviceId: string;
  deviceName: string;
  deviceFingerprintHash?: string; // Hash of the device fingerprint for comparison
  osName: string;
  osVersion: string;
  browserName: string;
  browserVersion: string;
  verifiedAt: string;
  firstUsedAt: string;
  lastUsedAt: string;
  totalUses: number;
  isActive: boolean;
  revokedAt?: string;
}

export interface AccountHealthScore {
  userId: string;
  healthScore: number;
  healthBand: "green" | "yellow" | "orange" | "red";
  sessionScore: number;
  deviceScore: number;
  anomalyScore: number;
  verificationScore: number;
  restrictionLevel: "none" | "gentle" | "moderate" | "strict";
  sessionOverlapCount: number;
  deviceChurnCount: number;
  geoAnomalyCount: number;
  updatedAt: string;
}

export interface AnomalyEvent {
  id: string;
  userId: string;
  sessionId?: string;
  eventType: "rapid_location_change" | "os_switch" | "overlapping_session" | "device_churn";
  severity: "low" | "medium" | "high";
  details: Record<string, unknown>;
  scoreImpact: number;
  isResolved: boolean;
  createdAt: string;
}

export interface PlanLimits {
  planName: string;
  maxActiveSessions: number;
  sessionIdleTimeoutMinutes: number;
  sessionAbsoluteTimeoutHours: number;
  maxTrustedDevices: number;
  deviceVerificationRequired: boolean;
  maxUsers: number;
  seatBased: boolean;
  minHealthScoreForFullAccess: number;
}

export interface DeviceFingerprint {
  deviceId: string;
  osName: string;
  osVersion: string;
  browserName: string;
  browserVersion: string;
  screenResolution: string;
  timezone: string;
  language: string;
  platform: string;
}

// ============================================
// Device Fingerprinting (centralized)
// ============================================

import { generateDeviceFingerprint as genDeviceFp, getDeviceName as dfGetDeviceName } from './deviceFingerprint';

export function deriveDeviceFingerprint() {
  return genDeviceFp();
}

// Backwards-compatible export
export const generateDeviceFingerprint = deriveDeviceFingerprint;

export function getDeviceName(fingerprint: DeviceFingerprint): string {
  return dfGetDeviceName(fingerprint);
}

// ============================================
// Session Management
// ============================================

export async function createSession(
  userId: string,
  sessionToken: string,
  fingerprint: DeviceFingerprint,
  ipAddress?: string,
  planName: string = "personal",
  csrfToken?: string
): Promise<{ success: boolean; sessionId?: string; error?: string; needsDeviceVerification?: boolean }> {
  try {
    const supabase = await getSupabaseClient();
    // Check if device is trusted
    const { data: trustedDevice } = await supabase
      .from("trusted_devices")
      .select("*")
      .eq("user_id", userId)
      .eq("device_id", fingerprint.deviceId)
      .eq("is_active", true)
      .single() as { data: TrustedDeviceRow | null; error?: PostgrestError | null };

    const isTrustedDevice = !!trustedDevice;

    // If device is not trusted and plan requires verification, return early
    if (!isTrustedDevice) {
      const { data: planLimits } = await supabase
        .from("plan_limits")
        .select("device_verification_required")
        .eq("plan_name", planName)
        .single() as { data: PlanLimitRow | null; error?: PostgrestError | null };

      if (planLimits?.device_verification_required) {
        return {
          success: false,
          needsDeviceVerification: true,
          error: "Device verification required",
        };
      }
    }

    // Enforce session limit
    await supabase.rpc("enforce_session_limit", {
      p_user_id: userId,
      p_plan_name: planName,
    });

    // Create session
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 24); // 24 hour absolute timeout

    const insertObj: Omit<UserSessionRow, 'id' | 'created_at' | 'last_activity_at' | 'ended_at'> & { device_fingerprint: DeviceFingerprint; csrf_token?: string } = {
      user_id: userId,
      session_token: sessionToken,
      device_id: fingerprint.deviceId,
      device_name: getDeviceName(fingerprint),
      os_name: fingerprint.osName,
      os_version: fingerprint.osVersion,
      browser_name: fingerprint.browserName,
      browser_version: fingerprint.browserVersion,
      ip_address: ipAddress,
      is_trusted_device: isTrustedDevice,
      expires_at: expiresAt.toISOString(),
      device_fingerprint: fingerprint,
    };

    if (csrfToken) insertObj.csrf_token = csrfToken;

    const { data, error } = await supabase
      .from("user_sessions")
      .insert(insertObj);

    if (error || !data) {
      throw new Error('Failed to create session');
    }

    // Update trusted device last used
    if (isTrustedDevice && trustedDevice) {
      await supabase
        .from("trusted_devices")
        .update({
          last_used_at: new Date().toISOString(),
          total_uses: trustedDevice.total_uses + 1,
        })
        .eq("id", trustedDevice.id);
    }

    const recs = data as unknown as Array<Record<string, unknown>>;
    return { success: true, sessionId: recs?.[0]?.id as string | undefined };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : "Failed to create session";
    return { success: false, error: message };
  }
}

export async function updateSessionActivity(sessionToken: string): Promise<boolean> {
  try {
    const supabase = await getSupabaseClient();
    const { error } = await supabase.rpc("update_session_activity", {
      p_session_token: sessionToken,
    });

    return !error;
  } catch {
    return false;
  }
}

export async function endSession(sessionToken: string): Promise<boolean> {
  try {
    const supabase = await getSupabaseClient();
    const { error } = await supabase
      .from("user_sessions")
      .update({ ended_at: new Date().toISOString() })
      .eq("session_token", sessionToken);

    return !error;
  } catch {
    return false;
  }
}

export async function getUserSessions(userId: string): Promise<UserSession[]> {
  try {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase
      .from("user_sessions")
      .select("*")
      .eq("user_id", userId)
      .is("ended_at", null)
      .order("last_activity_at", { ascending: false });

    if (error) throw error;
    return (data as unknown as UserSession[]) || [];
  } catch {
    return [];
  }
}

// ============================================
// Trusted Device Management
// ============================================

export async function verifyAndTrustDevice(
  userId: string,
  fingerprint: DeviceFingerprint,
  verificationMethod: "email" | "otp" | "authenticator" = "email",
  planName: string = "personal"
): Promise<{ success: boolean; deviceId?: string; error?: string; atLimit?: boolean }> {
  try {
    const supabase = await getSupabaseClient();
    // Check device limit
    const { data: canAdd } = await supabase.rpc("check_device_trust_limit", {
      p_user_id: userId,
      p_plan_name: planName,
    }) as unknown as { data: boolean | null; error?: PostgrestError | null };

    if (!canAdd) {
      return {
        success: false,
        atLimit: true,
        error: "Device limit reached. Please remove an old device first.",
      };
    }

    // Hash fingerprint for uniqueness
    const fingerprintHash = btoa(JSON.stringify(fingerprint)).substring(0, 64);

    // Create trusted device
    const { data, error } = await supabase
      .from("trusted_devices")
      .insert({
        user_id: userId,
        device_id: fingerprint.deviceId,
        device_fingerprint_hash: fingerprintHash,
        device_name: getDeviceName(fingerprint),
        os_name: fingerprint.osName,
        os_version: fingerprint.osVersion,
        browser_name: fingerprint.browserName,
        browser_version: fingerprint.browserVersion,
        verification_method: verificationMethod,
      })
      .select("id")
      .single() as unknown as { data: { id: string } | null; error?: PostgrestError | null };

    if (error) throw error;

    return { success: true, deviceId: data?.id };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : "Failed to trust device";
    return { success: false, error: message };
  }
}

export async function getTrustedDevices(userId: string): Promise<TrustedDevice[]> {
  try {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase
      .from("trusted_devices")
      .select("*")
      .eq("user_id", userId)
      .eq("is_active", true)
      .order("last_used_at", { ascending: false });

    if (error) throw error;
    return (data as unknown as TrustedDevice[]) || [];
  } catch {
    return [];
  }
}

export async function revokeDevice(
  deviceId: string,
  reason: string = "User requested"
): Promise<{ success: boolean; error?: string }> {
  try {
    const supabase = await getSupabaseClient();
    const { error } = await supabase
      .from("trusted_devices")
      .update({
        is_active: false,
        revoked_at: new Date().toISOString(),
        revoked_reason: reason,
      })
      .eq("id", deviceId);

    if (error) throw error;
    return { success: true };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : "Failed to revoke device";
    return { success: false, error: message };
  }
}

// ============================================
// Account Health Scoring
// ============================================

export async function getAccountHealth(userId: string): Promise<AccountHealthScore | null> {
  try {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase
      .from("account_health_scores")
      .select("*")
      .eq("user_id", userId)
      .single();

    if (error && error.code !== "PGRST116") throw error;

    return (data as unknown as AccountHealthScore) || null;
  } catch {
    return null;
  }
}

export async function recordAnomaly(
  userId: string,
  sessionId: string | undefined,
  eventType: AnomalyEvent["eventType"],
  severity: AnomalyEvent["severity"] = "low",
  details: Record<string, unknown> = {},
  scoreImpact: number = 5
): Promise<{ success: boolean; eventId?: string }> {
  try {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase.rpc("record_anomaly_event", {
      p_user_id: userId,
      p_session_id: sessionId,
      p_event_type: eventType,
      p_severity: severity,
      p_details: details,
      p_score_impact: scoreImpact,
    }) as unknown as { data: string | null; error?: PostgrestError | null };

    if (error) throw error;
    return { success: true, eventId: data || undefined };
  } catch {
    return { success: false };
  }
}

// ============================================
// Plan Limits
// ============================================

export async function getPlanLimits(planName: string): Promise<PlanLimits | null> {
  try {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase
      .from("plan_limits")
      .select("*")
      .eq("plan_name", planName)
      .single();

    if (error || !data) return null;

    const row = data as Record<string, unknown>;
    return {
      planName: String(row.plan_name),
      maxActiveSessions: Number(row.max_active_sessions ?? 0),
      sessionIdleTimeoutMinutes: Number(row.session_idle_timeout_minutes ?? 0),
      sessionAbsoluteTimeoutHours: Number(row.session_absolute_timeout_hours ?? 0),
      maxTrustedDevices: Number(row.max_trusted_devices ?? 0),
      deviceVerificationRequired: Boolean(row.device_verification_required),
      maxUsers: Number(row.max_users ?? 0),
      seatBased: Boolean(row.seat_based),
      minHealthScoreForFullAccess: Number(row.min_health_score_for_full_access ?? 0),
    } satisfies PlanLimits;
  } catch {
    return null;
  }
}

// ============================================
// User-Friendly Messages
// ============================================

export const INTEGRITY_MESSAGES = {
  sessionLimitReached: {
    title: "New device sign-in",
    description: "You've signed in on a new device. To keep your account secure, we signed you out elsewhere.",
  },
  deviceLimitReached: {
    title: "Device limit reached",
    description: "You've reached your trusted device limit. You can remove an old device to continue.",
  },
  deviceVerificationRequired: {
    title: "Verify this device",
    description: "For your security, please confirm it's you by verifying this device.",
  },
  healthYellow: {
    title: "Account activity notice",
    description: "We noticed unusual activity on your account. For your security, please verify it's you.",
  },
  healthOrange: {
    title: "Security verification required",
    description: "To protect your account, please complete verification before continuing.",
  },
  healthRed: {
    title: "Account temporarily restricted",
    description: "We've detected suspicious activity. Please contact support to restore full access.",
  },
};

// ============================================
// Anomaly Detection Helpers
// ============================================

export function detectRapidLocationChange(
  previousGeo: { lat: number; lon: number } | null,
  currentGeo: { lat: number; lon: number },
  timeElapsedMinutes: number
): boolean {
  if (!previousGeo) return false;

  // Calculate distance using Haversine formula
  const R = 6371; // Earth's radius in km
  const dLat = (currentGeo.lat - previousGeo.lat) * Math.PI / 180;
  const dLon = (currentGeo.lon - previousGeo.lon) * Math.PI / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(previousGeo.lat * Math.PI / 180) * Math.cos(currentGeo.lat * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  // If traveled more than 500km in less than 60 minutes, flag as anomaly
  const threshold = 500; // km
  const timeThreshold = 60; // minutes

  return distance > threshold && timeElapsedMinutes < timeThreshold;
}

export function detectDeviceChurn(deviceSwitchCount: number, timePeriodDays: number): boolean {
  // If switching devices more than 5 times in 7 days, flag as churn
  return deviceSwitchCount > 5 && timePeriodDays <= 7;
}

export function detectOverlappingSession(
  activeSessions: UserSession[],
  currentSession: UserSession
): boolean {
  // Check if multiple sessions are active simultaneously from different locations
  const otherActiveSessions = activeSessions.filter(s => s.id !== currentSession.id);

  for (const session of otherActiveSessions) {
    // If sessions overlap in time and are from different locations
    if (
      session.geoCountry &&
      currentSession.geoCountry &&
      session.geoCountry !== currentSession.geoCountry
    ) {
      return true;
    }
  }

  return false;
}
